import { useMemo, useRef, useState } from 'react';
import { useReactFlow, useViewport } from '@xyflow/react';
import { useFlowBuilderStore, nodeTypeConfig } from '../../stores/flowBuilderStore';
import { useAppStore } from '../../stores/appStore';
import { api } from '../../lib/api';
import { toast } from '../../stores/toastStore';
import {
  Play,
  MessageSquare,
  Image,
  File,
  Grid,
  Layers,
  Edit,
  List,
  GitBranch,
  GitMerge,
  Shuffle,
  Globe,
  Clock,
  Sparkles,
  Brain,
  Heart,
  User,
  Users,
  XCircle,
  UserPlus,
  UserCheck,
  Tag,
  ArrowRight,
  Box,
  Key,
  Hash,
  Search,
  HelpCircle,
  EyeOff,
  ChevronDown,
  Plus,
  Minus,
  Maximize2,
  Network,
  Undo2,
  Redo2,
  IdCard,
  Mic,
  Scan,
  Braces,
  Database,
} from 'lucide-react';
import type { NodeType } from '../../types';

const iconMap: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  play: Play,
  key: Key,
  'message-square': MessageSquare,
  image: Image,
  file: File,
  grid: Grid,
  layers: Layers,
  edit: Edit,
  list: List,
  'git-branch': GitBranch,
  'git-merge': GitMerge,
  shuffle: Shuffle,
  variable: Hash,
  globe: Globe,
  clock: Clock,
  sparkles: Sparkles,
  brain: Brain,
  heart: Heart,
  user: User,
  users: Users,
  'x-circle': XCircle,
  'user-plus': UserPlus,
  'user-check': UserCheck,
  tag: Tag,
  'arrow-right': ArrowRight,
  box: Box,
  search: Search,
  'help-circle': HelpCircle,
  'eye-off': EyeOff,
  'id-card': IdCard,
  mic: Mic,
  scan: Scan,
  braces: Braces,
  database: Database,
};

const categories = [
  { id: 'message', label: 'Mensagens', color: '#3b82f6', icon: MessageSquare },
  { id: 'logic', label: 'Lógica', color: '#8b5cf6', icon: GitBranch },
  { id: 'action', label: 'Ações', color: '#ef4444', icon: ArrowRight },
  { id: 'integration', label: 'Integrações', color: '#06b6d4', icon: Sparkles },
];

interface NodePaletteProps {
  onReorganize?: () => void;
  canReorganize?: boolean;
  onTest?: () => void;
}

const AI_NODE_TYPES: NodeType[] = ['AI_RESPONSE', 'AI_TRANSCRIBE_AUDIO', 'AI_ANALYZE_IMAGE'];
const AI_KEY_STATUS_CACHE_TTL_MS = 60_000;

function BarButton({
  onClick,
  title,
  disabled,
  children,
}: {
  onClick?: () => void;
  title: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="grid h-8 w-8 place-items-center rounded-lg text-slate-300 transition-colors hover:bg-slate-800 hover:text-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}

/**
 * Unified bottom bar (Flowbuild): Testar fluxo | zoom − % + · enquadrar · organizar | paleta de passos.
 * Keeps the legacy export name so FlowBuilder's import is unchanged.
 */
export function NodePalette({ onReorganize, canReorganize = true, onTest }: NodePaletteProps) {
  const { addNode } = useFlowBuilderStore();
  const undo = useFlowBuilderStore((state) => state.undo);
  const redo = useFlowBuilderStore((state) => state.redo);
  const canUndo = useFlowBuilderStore((state) => state.past.length > 0);
  const canRedo = useFlowBuilderStore((state) => state.future.length > 0);
  const tenant = useAppStore((state) => state.tenant);
  const { getViewport, zoomIn, zoomOut, fitView } = useReactFlow();
  const { zoom } = useViewport();
  const [activeCategory, setActiveCategory] = useState<string>('message');
  const [isExpanded, setIsExpanded] = useState(false);
  const aiKeyCacheRef = useRef<{
    checkedAt: number;
    openaiConfigured: boolean;
    geminiConfigured: boolean;
  } | null>(null);

  const isAiNode = (type: NodeType) => AI_NODE_TYPES.includes(type);

  const warnIfAiKeysNotConfigured = async (): Promise<void> => {
    if (!tenant?.id) return;

    const now = Date.now();
    const cached = aiKeyCacheRef.current;
    if (cached && now - cached.checkedAt < AI_KEY_STATUS_CACHE_TTL_MS) {
      if (!cached.openaiConfigured && !cached.geminiConfigured) {
        toast.error(
          'Nenhuma chave de IA está configurada. Configure OpenAI ou Gemini em Configurações → Integrações.',
          'Integrações de IA',
          6000,
        );
      }
      return;
    }

    try {
      const [openaiStatus, geminiStatus] = await Promise.all([
        api.getOpenAIKeyStatus(tenant.id),
        api.getGeminiKeyStatus(tenant.id),
      ]);

      aiKeyCacheRef.current = {
        checkedAt: now,
        openaiConfigured: openaiStatus.configured === true,
        geminiConfigured: geminiStatus.configured === true,
      };

      if (!openaiStatus.configured && !geminiStatus.configured) {
        toast.error(
          'Nenhuma chave de IA está configurada. Configure OpenAI ou Gemini em Configurações → Integrações.',
          'Integrações de IA',
          6000,
        );
      }
    } catch {
      // Non-blocking: key check failed but node can still be added
    }
  };

  const handleAddNode = async (type: NodeType) => {
    // Add node immediately so the canvas responds at once
    const vp = getViewport();
    const centerX = (window.innerWidth / 2 - vp.x) / vp.zoom;
    const centerY = (window.innerHeight / 2 - vp.y) / vp.zoom;
    addNode(type, { x: centerX, y: centerY });
    setIsExpanded(false);

    // Warn about missing AI keys right after placement (awaited = fires immediately)
    if (isAiNode(type)) {
      await warnIfAiKeysNotConfigured();
    }
  };

  const nodesByCategory = useMemo(
    () =>
      Object.entries(nodeTypeConfig).reduce(
        (acc, [type, config]) => {
          if (type === 'START' || type === 'KEYWORD_TRIGGER') return acc; // Skip trigger nodes
          if (config.hidden) return acc; // Skip hidden nodes
          if (!acc[config.category]) acc[config.category] = [];
          acc[config.category].push({ type: type as NodeType, ...config });
          return acc;
        },
        {} as Record<string, Array<{ type: NodeType; label: string; color: string; icon: string; comingSoon?: boolean }>>,
      ),
    [],
  );

  const selectedCategory = categories.find((category) => category.id === activeCategory) || categories[0];
  const currentNodes = nodesByCategory[selectedCategory.id] || [];

  return (
    <div className="pointer-events-none fixed bottom-4 left-1/2 z-40 flex max-w-[calc(100vw-28px)] -translate-x-1/2 flex-col items-center gap-2">
      {isExpanded && (
        <div className="pointer-events-auto w-[min(880px,calc(100vw-2rem))] rounded-xl border border-slate-700/70 bg-slate-900 px-3 py-3 shadow-[0_18px_50px_rgba(0,0,0,0.6)]">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-baseline gap-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-violet-300">
                {selectedCategory.label}
              </p>
              <p className="text-[10.5px] text-slate-500">clique para adicionar · ou arraste para o canvas</p>
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              className="rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
              title="Recolher"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-4">
            {currentNodes.map((node) => {
              const Icon = iconMap[node.icon] || MessageSquare;
              return (
                <button
                  key={node.type}
                  onClick={() => !node.comingSoon && void handleAddNode(node.type)}
                  disabled={node.comingSoon}
                  draggable={!node.comingSoon}
                  onDragStart={(event) => {
                    event.dataTransfer.setData('application/reactflow', node.type);
                    event.dataTransfer.effectAllowed = 'move';
                  }}
                  title={node.comingSoon ? 'Em breve' : 'Clique para adicionar no centro ou arraste para o canvas'}
                  className={`flex items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-colors ${
                    node.comingSoon
                      ? 'cursor-not-allowed border-transparent opacity-40'
                      : 'border-transparent hover:border-slate-700 hover:bg-slate-800'
                  }`}
                >
                  <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md border border-white/5 bg-slate-800">
                    <Icon className="h-4 w-4" style={{ color: node.color }} />
                  </span>
                  <span className="flex-1 truncate text-[12.5px] text-slate-200">{node.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="pointer-events-auto flex items-center rounded-xl border border-slate-700/70 bg-slate-900 p-1.5 shadow-[0_18px_50px_rgba(0,0,0,0.6)]">
        {onTest && (
          <>
            <button
              type="button"
              onClick={onTest}
              className="flex h-[34px] items-center gap-2 whitespace-nowrap rounded-lg bg-violet-600 px-3.5 text-[13px] font-medium text-white transition-colors hover:bg-violet-500"
              title="Testar fluxo (simulação local)"
            >
              <Play className="h-3.5 w-3.5" />
              Testar fluxo
            </button>
            <span className="mx-2 h-[26px] w-px bg-slate-700" />
          </>
        )}

        <div className="flex items-center gap-0.5">
          <BarButton onClick={undo} disabled={!canUndo} title="Desfazer (Ctrl Z)">
            <Undo2 className="h-4 w-4" />
          </BarButton>
          <BarButton onClick={redo} disabled={!canRedo} title="Refazer (Ctrl Shift Z)">
            <Redo2 className="h-4 w-4" />
          </BarButton>
        </div>

        <span className="mx-2 h-[26px] w-px bg-slate-700" />

        <div className="flex items-center gap-0.5">
          <BarButton onClick={() => void zoomOut({ duration: 150 })} title="Diminuir zoom">
            <Minus className="h-4 w-4" />
          </BarButton>
          <span className="w-10 text-center text-[11.5px] tabular-nums text-slate-300">
            {Math.round(zoom * 100)}%
          </span>
          <BarButton onClick={() => void zoomIn({ duration: 150 })} title="Aumentar zoom">
            <Plus className="h-4 w-4" />
          </BarButton>
          <BarButton onClick={() => void fitView({ padding: 0.18, duration: 300 })} title="Enquadrar">
            <Maximize2 className="h-4 w-4" />
          </BarButton>
          {onReorganize && (
            <BarButton onClick={onReorganize} disabled={!canReorganize} title="Organizar layout">
              <Network className="h-4 w-4" />
            </BarButton>
          )}
        </div>

        <span className="mx-2 h-[26px] w-px bg-slate-700" />

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setIsExpanded((previous) => !previous)}
            className="grid h-8 w-8 place-items-center rounded-full bg-violet-600 text-white transition-colors hover:bg-violet-500"
            title="Adicionar passo"
          >
            <Plus className="h-4 w-4" />
          </button>
          {categories.map((category) => {
            const Icon = category.icon;
            const isActive = isExpanded && category.id === selectedCategory.id;
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => {
                  setActiveCategory(category.id);
                  setIsExpanded(true);
                }}
                title={category.label}
                className={`grid h-8 w-8 place-items-center rounded-full transition-colors ${
                  isActive ? 'bg-violet-500/25 ring-1 ring-violet-500' : 'bg-slate-800 hover:bg-slate-700'
                }`}
              >
                <Icon className="h-4 w-4" style={{ color: category.color }} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
